import Vue from 'vue';
import { createHelpers } from 'vuex-map-fields';

import {
  CARESERVICE_REMOVE_STEP,
  CARESERVICE_ADD_STEP,
  CARESERVICE_UPDATE_FACT,
  CARESERVICE_UPDATE_CARELEVEL,
  CARESERVICE_SAVE,
  CARESERVICE_REQUEST,
  CARESERVICE_CLEAR_WIZARD,
} from '../actions/careService';

import { ROUTE_API_GET_CARE_SERVICE, ROUTE_API_SAVE_CARE_SERVICE } from '@/constants/apiRoutes';

const { getCareServiceField, updateCareServiceField } = createHelpers({
  getterType: 'getCareServiceField',
  mutationType: 'updateCareServiceField',
});

function getDefaultCareServiceFacts() {
  return {
    entlastungsbetrag: null,
    hausnotruf: null,
    kombinationsleistung: null,
    kurzzeitpflege: null,
    pflegegeld: 2,
    pflegehilfsmittel: null,
    pflegesachleistungen: null,
    tagesundnachtpflege: null,
    verhinderungspflege: null,
    wohnumfeldverbesserungen: null,
  };
}

// const testData = {
//   careService: {
//     'switcherRow-0': [2, 1],
//     'switcherRow-1': [1, 1],
//     'switcherRow-2': [2, 0],
//   },
//   careServiceFacts: {
//     pflegegeld: 0,
//     pflegesachleistungen: 0,
//     kombinationsleistung: 2,
//     entlastungsbetrag: 2,
//     hausnotruf: 1,
//     kurzzeitpflege: 1,
//     pflegehilfsmittel: 1,
//     tagesundnachtpflege: 2,
//     verhinderungspflege: 1,
//     wohnumfeldverbesserungen: 2,
//   },
//   careServiceWizardIsFinished: true,
// };

const state = {
  /**
   * Prüft im Wizard, ob ein Schritt valide ist.
   */
  careServiceStepIsValid: false,

  /**
   * User Input aus dem Wizard.
   *
   * Beispiel:
   *   'switcherRow-0': [2, 1],
   *   'switcherRow-1': [1, 1],
   *   'switcherRow-2': [2, 0],
   *
   * Das erste Arrayelement ist die Antwort aus "Wie häufig benötigt die betroffene Person dabei schätzungsweise Unterstützung?"
   * Das zweite Arrayelement ist die Antwort aus "Möchten Sie oder andere Angehörige die Tätigkeiten übernehmen?"
   */
  careService: {},

  /**
   * Informationen über die Pflegeleistungen:
   *
   * Jedes Element kann die Zustände 0, 1, 2 und 3 annehmen
   *
   * 0: User WÜNSCHT sich NICHT diese Leistung
   * 1: User WÜNSCHT sich diese Leistung
   * 2: User HAT diese Leistung
   * 3: User HAT diese Leistung (identisch mit 2).
   *    Wird für Berechnung der Leistungen nicht berücksichtigt
   *
   * Diese Elemente werden sowohl im letzten Schritt vom Wizard gesetzt,
   * als auch auf der Analyseseite.
   */
  careServiceFacts: getDefaultCareServiceFacts(),

  /**
   * Information, ob der Wizard durchlaufen wurde
   * oder ob der User direkt auf die Analyseseite geht
   */
  careServiceWizardIsFinished: false,

  /**
   * careServiceCareLevel wird benötigt,
   * weil auf der Analyseseite der Pflegegrad ausgewählt
   * werden kann, wenn der errechnete Pflegegrad vom
   * tatsächlichen Pflegegrad abweicht.
   */
  careServiceCareLevel: null,

  /**
   * Auf der Analyseseite hat die Kombinationsleistung
   * zusätzlich einen Slider, um das Verhältnis zwischen
   * Pflegegeld und Pflegesachleistungen anzupassen.
   * Der value ist ein String, weil der Range Slider im v-model
   * den Wert als zurück gibt.
   */
  userInputValue: '50',

  // ...testData, // simuliere absolvierten Wizard
};

const getters = {
  getCareServiceField,
};

const mutations = {
  updateCareServiceField,

  [CARESERVICE_UPDATE_FACT]: (stata, data) => {
    state.careServiceFacts = { ...data };
  },

  [CARESERVICE_UPDATE_CARELEVEL]: (state, { careServiceCareLevel }) => {
    state.careServiceCareLevel = careServiceCareLevel;
  },

  [CARESERVICE_REQUEST]: (state, data) => {
    state = Object.assign(state, data);
  },

  [CARESERVICE_CLEAR_WIZARD]: (state) => {
    state.careService = {};
    state.careServiceStepIsValid = false;
    state.careServiceWizardIsFinished = false;
    state.userInputValue = '50';
  },

  CARESERVICE_UPDATE_STEP: (state, data) => {
    state.careService = { ...data };
  },
};

const actions = {
  [CARESERVICE_CLEAR_WIZARD]: ({ commit }) => {
    commit(CARESERVICE_CLEAR_WIZARD);
  },

  [CARESERVICE_ADD_STEP]: ({ commit }, data) => {
    const { id, arr } = data;
    const newState = state.careService;
    newState[id] = arr;

    commit('CARESERVICE_UPDATE_STEP', newState);
  },

  [CARESERVICE_UPDATE_FACT]: ({ commit, dispatch }, data) => {
    return new Promise((resolve, reject) => {
      const { id, value } = data;
      const newState = state.careServiceFacts;
      newState[id] = value;

      commit(CARESERVICE_UPDATE_FACT, newState);
      dispatch(CARESERVICE_SAVE).then(resolve).catch(reject);
    });
  },

  [CARESERVICE_REMOVE_STEP]: ({ commit }, data) => {
    const { id } = data;
    const newState = state.careService;
    delete newState[id];

    commit('CARESERVICE_UPDATE_STEP', newState);
  },

  [CARESERVICE_UPDATE_CARELEVEL]: ({ commit }, data) => {
    const { careServiceCareLevel } = data;
    commit(CARESERVICE_UPDATE_CARELEVEL, { careServiceCareLevel });
  },

  [CARESERVICE_SAVE]: ({ rootState, state, dispatch }, d) => {
    return new Promise((resolve, reject) => {
      const saveCareService =
        !!d?.careServiceWizardIsFinished || Object.keys(state.careService).length > 0;

      const data = {
        uuid: rootState.health.healthUuid,
        userInputValue: d?.userInputValue || state.userInputValue,
        careServiceFacts: JSON.stringify(state.careServiceFacts),
        careService: saveCareService ? JSON.stringify(state.careService) : null,
      };

      Vue.axios
        .post(ROUTE_API_SAVE_CARE_SERVICE, data)
        .then(resolve)
        .catch(() => {
          /**
           * Wenn es einen Fehler bei der Speicherung gibt,
           * dann sollten die Werte zurückgesetzt werden, in dem
           * die aktuellen Werte aus der Datenbank geholt werden,
           */
          dispatch(CARESERVICE_REQUEST).then(reject).catch(reject);
        });
    });
  },

  [CARESERVICE_REQUEST]: ({ commit, rootState, state }) => {
    return new Promise((resolve, reject) => {
      if (state.careServiceWizardIsFinished) {
        resolve();
      } else {
        Vue.axios
          .post(ROUTE_API_GET_CARE_SERVICE, {
            uuid: rootState.health.healthUuid,
          })
          .then((response) => {
            let { careService, careServiceFacts, userInputValue } = response.data;

            const data = {};

            if (careService) {
              data.careService = JSON.parse(careService);
              data.careServiceWizardIsFinished = true;
            }

            if (careServiceFacts) {
              data.careServiceFacts = JSON.parse(careServiceFacts);
            }

            if (userInputValue) {
              data.userInputValue = userInputValue;
            }

            commit(CARESERVICE_REQUEST, data);
            resolve();
          })
          .catch(reject);
      }
    });
  },
};

export default {
  state,
  getters,
  mutations,
  actions,
};
